
const DAY_MS = 24 * 60 * 60 * 1000;

export const POST_TYPES = Object.freeze({
  LOST: 'lost',
  FOUND: 'found',
  PSA: 'psa',
  PET_SERVICE: 'petService',
});

export const POST_LIFETIMES = Object.freeze({
  [POST_TYPES.LOST]: 30,
  [POST_TYPES.FOUND]: 30,
  [POST_TYPES.PSA]: 30,
  [POST_TYPES.PET_SERVICE]: 60,
});

export const POST_STATUS = Object.freeze({
  ACTIVE: 'active',
  EXPIRING: 'expiring',
  EXPIRING_SOON: 'expiring-soon',
  EXPIRED: 'expired',
  UNKNOWN: 'unknown',
});

export const POST_STATUS_TONES = Object.freeze({
  GREEN: 'green',
  AMBER: 'amber',
  ORANGE: 'orange',
  RED: 'red',
  GRAY: 'gray',
});

export function normalizePostTypeKey(rawType) {
  if (!rawType || typeof rawType !== 'string') return null;
  const key = rawType.trim().toLowerCase();
  switch (key) {
    case 'lost':
    case 'lostanimal':
    case 'lost pet':
      return POST_TYPES.LOST;
    case 'found':
    case 'foundanimal':
    case 'found pet':
      return POST_TYPES.FOUND;
    case 'psa':
      return POST_TYPES.PSA;
    case 'petservice':
    case 'pet service':
    case 'pet_service':
      return POST_TYPES.PET_SERVICE;
    default:
      return null;
  }
}

export function getPostLifetimeDays(postType) {
  const normalized = normalizePostTypeKey(postType) || postType;
  return POST_LIFETIMES[normalized] ?? 30;
}

const EPOCH_MS_STRING = /^-?\d{10,}$/;

export function parseExpiresAt(expiresAt) {
  if (expiresAt == null || expiresAt === '') return null;
  if (expiresAt instanceof Date) {
    return Number.isNaN(expiresAt.getTime()) ? null : expiresAt;
  }
  if (typeof expiresAt === 'number') {
    const d = new Date(expiresAt);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  if (typeof expiresAt === 'string') {

    if (EPOCH_MS_STRING.test(expiresAt)) {
      const d = new Date(Number(expiresAt));
      return Number.isNaN(d.getTime()) ? null : d;
    }

    const d = new Date(expiresAt);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  return null;
}

export function getPostStatus(post, now = new Date()) {
  const expiresAt = parseExpiresAt(post && post.expiresAt);
  if (!expiresAt) {
    return {
      status: POST_STATUS.UNKNOWN,
      tone: POST_STATUS_TONES.GRAY,
      daysLeft: null,
      expiresAt: null,
    };
  }

  const diffMs = expiresAt.getTime() - now.getTime();

  const daysLeft = diffMs >= 0
    ? Math.floor(diffMs / DAY_MS)
    : Math.ceil(diffMs / DAY_MS) - 1;

  if (diffMs < 0) {
    return {
      status: POST_STATUS.EXPIRED,
      tone: POST_STATUS_TONES.RED,
      daysLeft,
      expiresAt,
    };
  }
  if (daysLeft <= 1) {
    return {
      status: POST_STATUS.EXPIRING_SOON,
      tone: POST_STATUS_TONES.ORANGE,
      daysLeft,
      expiresAt,
    };
  }
  if (daysLeft <= 7) {
    return {
      status: POST_STATUS.EXPIRING,
      tone: POST_STATUS_TONES.AMBER,
      daysLeft,
      expiresAt,
    };
  }
  return {
    status: POST_STATUS.ACTIVE,
    tone: POST_STATUS_TONES.GREEN,
    daysLeft,
    expiresAt,
  };
}


export function formatExpirationDate(expiresAt, locale) {
  const d = parseExpiresAt(expiresAt);
  if (!d) return '';
  try {
    return new Intl.DateTimeFormat(locale || undefined, {
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
    }).format(d);
  } catch (_err) {

    return d.toISOString().slice(0, 10);
  }
}


export function wasRecentlyExtended(post, now = new Date()) {
  const last = parseExpiresAt(post && post.lastExtendedAt);
  if (!last) return false;
  return now.getTime() - last.getTime() < DAY_MS;
}

export const EXTEND_WINDOW_DAYS = 7;

export const EXTEND_REASONS = Object.freeze({
  ELIGIBLE: 'eligible',
  TOO_EARLY: 'too-early',
  UNKNOWN_EXPIRES_AT: 'unknown-expires-at',
});


export function canExtendPost(post, now = new Date()) {
  const expiresAt = parseExpiresAt(post && post.expiresAt);
  if (!expiresAt) {
    return { allowed: true, reason: EXTEND_REASONS.UNKNOWN_EXPIRES_AT };
  }
  const diffMs = expiresAt.getTime() - now.getTime();
  if (diffMs <= 0) {
    return { allowed: true, reason: EXTEND_REASONS.ELIGIBLE };
  }
  if (diffMs <= EXTEND_WINDOW_DAYS * DAY_MS) {
    return { allowed: true, reason: EXTEND_REASONS.ELIGIBLE };
  }
  return { allowed: false, reason: EXTEND_REASONS.TOO_EARLY };
}
